import { Users, Target, Briefcase, HeartHandshake, Shield, TrendingUp, Mail, Phone } from "lucide-react";

export default function About() {
  return (
    <div className="page-container">
      <div className="content-wrapper">
        <div className="page-header">
          <h1 className="page-title">About Second Chance Connect</h1>
          <p className="text-xl text-muted-foreground mt-4 max-w-3xl mx-auto">
            Breaking down barriers to employment for people with criminal records across the UK
          </p>
        </div>
        
        <div className="prose space-y-8">
          <section className="section-card mb-8 hover:shadow-[var(--shadow-elevated)] transition-shadow">
            <div className="flex items-start gap-4 mb-4">
              <div className="bg-accent/10 p-3 rounded-full flex-shrink-0">
                <Target className="h-6 w-6 text-accent" />
              </div>
              <div>
                <h2 className="mb-4 mt-0">Our Mission</h2>
                <p>
                  Second Chance Connect helps people with criminal records find meaningful employment, stable housing, and practical support so they can rebuild lives with dignity and opportunity. We remove barriers, bridge relationships between employers and candidates, and create a supportive pathway from application to sustained work and community reintegration.
                </p>
              </div>
            </div>
          </section>

          <section className="section-card mb-8 hover:shadow-[var(--shadow-elevated)] transition-shadow">
            <div className="flex items-start gap-4 mb-4">
              <div className="bg-primary/10 p-3 rounded-full flex-shrink-0">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="mb-4 mt-0">Who We Serve</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                    <Users className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                    <div>
                      <strong className="text-foreground">Individuals with convicted or cautioned records</strong>
                      <p className="text-muted-foreground mt-1">Seeking fair access to jobs, training, and housing.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                    <Briefcase className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                    <div>
                      <strong className="text-foreground">Employers</strong>
                      <p className="text-muted-foreground mt-1">Willing to offer safe, inclusive opportunities and access practical guidance on fair hiring.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                    <HeartHandshake className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                    <div>
                      <strong className="text-foreground">Charities, probation services, and local support organisations</strong>
                      <p className="text-muted-foreground mt-1">Looking to coordinate referrals, training, and wraparound care.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="section-card mb-8 hover:shadow-[var(--shadow-elevated)] transition-shadow">
            <div className="flex items-start gap-4 mb-4">
              <div className="bg-accent/10 p-3 rounded-full flex-shrink-0">
                <Briefcase className="h-6 w-6 text-accent" />
              </div>
              <div>
                <h2 className="mb-4 mt-0">What We Do</h2>
                <ul className="list-disc ml-6 space-y-3">
                  <li className="text-foreground/90">Match candidates with employers that recognise rehabilitation and offer second-chance roles.</li>
                  <li className="text-foreground/90">Provide pre-employment coaching, CV support, interview preparation, and ongoing workplace mentoring.</li>
                  <li className="text-foreground/90">Offer practical support services including skills training, access to digital tools, and signposting to housing, legal, and mental health partners.</li>
                  <li className="text-foreground/90">Maintain strict privacy and safety practices for all users and partner organisations.</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="bg-gradient-to-br from-accent/5 to-primary/5 p-8 rounded-xl border border-accent/20 mb-8 shadow-[var(--shadow-card)] hover:shadow-[var(--shadow-elevated)] transition-shadow">
            <h2 className="mb-6 text-center">How It Works</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-card/80 backdrop-blur-sm p-6 rounded-lg shadow-sm border border-border hover-scale">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-accent/10 p-2 rounded-full">
                    <span className="text-2xl font-bold text-accent">1</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-0 mt-0">Register</h3>
                </div>
                <p className="text-muted-foreground">
                  Candidates create a confidential profile that highlights skills, experience, and support needs.
                </p>
              </div>
              <div className="bg-card/80 backdrop-blur-sm p-6 rounded-lg shadow-sm border border-border hover-scale">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-accent/10 p-2 rounded-full">
                    <span className="text-2xl font-bold text-accent">2</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-0 mt-0">Prepare</h3>
                </div>
                <p className="text-muted-foreground">
                  We help candidates build CVs, practice interviews, and complete tailored training modules.
                </p>
              </div>
              <div className="bg-card/80 backdrop-blur-sm p-6 rounded-lg shadow-sm border border-border hover-scale">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-accent/10 p-2 rounded-full">
                    <span className="text-2xl font-bold text-accent">3</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-0 mt-0">Match</h3>
                </div>
                <p className="text-muted-foreground">
                  Our team connects candidates to employers who have pledged to consider applicants with convictions and provides employers with risk-aware onboarding guidance.
                </p>
              </div>
              <div className="bg-card/80 backdrop-blur-sm p-6 rounded-lg shadow-sm border border-border hover-scale">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-accent/10 p-2 rounded-full">
                    <span className="text-2xl font-bold text-accent">4</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-0 mt-0">Support</h3>
                </div>
                <p className="text-muted-foreground">
                  We deliver follow-up mentoring, in-work support, and connections to partner services to reduce recidivism and increase retention.
                </p>
              </div>
            </div>
          </section>

          <section className="section-card mb-8 hover:shadow-[var(--shadow-elevated)] transition-shadow">
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-3 rounded-full flex-shrink-0">
                <HeartHandshake className="h-6 w-6 text-primary" />
              </div>
              <h2 className="mb-0 mt-0">Our Values</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-border p-5 rounded-lg bg-accent/5 hover:bg-accent/10 transition-colors">
                <h3 className="text-lg font-semibold mb-2 mt-0 text-foreground flex items-center gap-2">
                  <Shield className="h-5 w-5 text-accent" />
                  Respect
                </h3>
                <p className="text-muted-foreground">We treat every person with dignity and preserve confidentiality.</p>
              </div>
              <div className="border border-border p-5 rounded-lg bg-accent/5 hover:bg-accent/10 transition-colors">
                <h3 className="text-lg font-semibold mb-2 mt-0 text-foreground flex items-center gap-2">
                  <Target className="h-5 w-5 text-accent" />
                  Fairness
                </h3>
                <p className="text-muted-foreground">We advocate for equal opportunity and challenge stigma in hiring.</p>
              </div>
              <div className="border border-border p-5 rounded-lg bg-accent/5 hover:bg-accent/10 transition-colors">
                <h3 className="text-lg font-semibold mb-2 mt-0 text-foreground flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-accent" />
                  Practicality
                </h3>
                <p className="text-muted-foreground">We focus on measurable outcomes: jobs secured, people retained, and lives improved.</p>
              </div>
              <div className="border border-border p-5 rounded-lg bg-accent/5 hover:bg-accent/10 transition-colors">
                <h3 className="text-lg font-semibold mb-2 mt-0 text-foreground flex items-center gap-2">
                  <HeartHandshake className="h-5 w-5 text-accent" />
                  Partnership
                </h3>
                <p className="text-muted-foreground">We work with employers, statutory services, and community organisations to create realistic, sustainable pathways.</p>
              </div>
            </div>
          </section>

          <section className="section-card mb-8 hover:shadow-[var(--shadow-elevated)] transition-shadow">
            <div className="flex items-start gap-4 mb-4">
              <div className="bg-accent/10 p-3 rounded-full flex-shrink-0">
                <Briefcase className="h-6 w-6 text-accent" />
              </div>
              <div>
                <h2 className="mb-4 mt-0">Employers and Partners</h2>
                <p>
                  We provide employers with guidance on safe hiring practices, reasonable adjustments, and probation-friendly onboarding. Partner organisations gain a streamlined referral route and shared case-management tools to coordinate support and measure impact.
                </p>
              </div>
            </div>
          </section>

          <section className="section-card mb-8 hover:shadow-[var(--shadow-elevated)] transition-shadow">
            <div className="flex items-start gap-4 mb-4">
              <div className="bg-primary/10 p-3 rounded-full flex-shrink-0">
                <TrendingUp className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="mb-4 mt-0">Impact and Accountability</h2>
                <p>
                  We track outcomes including placements, retention at 3 and 12 months, and participant wellbeing measures. We publish aggregated, anonymised impact summaries and maintain robust data protection practices to safeguard sensitive information.
                </p>
              </div>
            </div>
          </section>

          <section className="bg-gradient-to-br from-primary/10 to-accent/10 p-8 rounded-xl border border-primary/20 mb-8 shadow-[var(--shadow-card)] hover:shadow-[var(--shadow-elevated)] transition-shadow">
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-accent/20 p-3 rounded-full flex-shrink-0">
                <Mail className="h-6 w-6 text-accent" />
              </div>
              <h2 className="mb-0 mt-0">Get Involved</h2>
            </div>
            <div className="space-y-4 bg-card/60 backdrop-blur-sm p-6 rounded-lg">
              <div className="flex items-start gap-3">
                <Users className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                <p className="mb-0">
                  <strong className="text-foreground">If you are seeking work or support:</strong> Create a confidential profile or contact our candidate support team at{" "}
                  <a href="mailto:admin@sccapp.co.uk" className="text-accent hover:underline font-semibold">
                    admin@sccapp.co.uk
                  </a> or call <a href="tel:+447718942231" className="text-accent hover:underline font-semibold">+44 07718942231</a>
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Briefcase className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                <p className="mb-0">
                  <strong className="text-foreground">If you are an employer:</strong> Interested in offering second-chance roles? Register as a partner to receive practical onboarding toolkits and candidate referrals.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <HeartHandshake className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                <p className="mb-0">
                  <strong className="text-foreground">If you represent a charity, probation service, or funder:</strong> Contact{" "}
                  <a href="mailto:admin@sccapp.co.uk" className="text-accent hover:underline font-semibold">
                    admin@sccapp.co.uk
                  </a>
                  {" "}to discuss collaboration.
                </p>
              </div>
            </div>
          </section>

          <div className="section-card bg-gradient-to-br from-accent/5 to-primary/5 border-accent/30 shadow-[var(--shadow-elevated)]">
            <div className="flex items-start gap-4">
              <div className="bg-accent/20 p-3 rounded-full flex-shrink-0">
                <HeartHandshake className="h-7 w-7 text-accent" />
              </div>
              <div>
                <h2 className="mb-3 mt-0">Closing Statement</h2>
                <p className="text-foreground text-lg leading-relaxed mb-0">
                  Second Chance Connect exists to turn past mistakes into future opportunities by combining practical support, trusted employer partnerships, and a commitment to fairness. We believe everyone deserves the chance to contribute, belong, and succeed.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
